import { useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import FeatureRenameDialog from "@/v2-backoffice-client/feature/rename/FeatureRenameDialog"
import BasemapActionMenu from "@/v2-backoffice-client/basemap/BasemapActionMenu"
import BasemapThumbnail from "@/v2-backoffice-client/basemap/BasemapThumbnail"
import { useBasemapQuery } from "@/v2-backoffice-client/basemap/basemap.query"
import type { BasemapFragment } from "@/v2-backoffice-client/basemap/__types__/basemap.fragment"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

const COLUMN_SIZING = {
  Thumbnail: 72,
  Priority: 60,
  IsEnabled: 90,
  CreatedAt: 130,
  UpdatedAt: 130,
}

const columns = createColumns<BasemapFragment>({
  enableSelection: true,
  enableSorting: true,
  enableHiding: true,
  columns: {
    Thumbnail: {
      id: "Thumbnail",
      label: "",
      // The chooser is a wall of thumbnails, so this is how an operator
      // recognises a basemap — worth a column of its own.
      enableSorting: false,
      renderCell({ row }) {
        return (
          <BasemapThumbnail
            src={row.original.Thumbnail}
            name={row.original.Name}
          />
        )
      },
    },
    Priority: { id: "Priority", label: "Order" },
    Name: {
      id: "Name",
      label: "Name",
      to: ({ row }) => `?v=basemap:${row.original.FeatureCode}`,
    },
    Description: {
      id: "Description",
      label: "Description",
      defaultHidden: true,
      renderCell({ row }) {
        const description = row.original.Description
        if (!description) return null
        return (
          <span className="truncate max-w-[200px]" title={description}>
            {description}
          </span>
        )
      },
    },
    FeatureCode: {
      id: "FeatureCode",
      label: "Feature Code",
      filter: { type: "text", placeholder: "Filter by code..." },
    },
    IsEnabled: {
      id: "IsEnabled",
      label: "Status",
      renderCell({ value }) {
        return (
          <DotText variant={value ? "success-1" : "destructive"}>
            {value ? "Enabled" : "Disabled"}
          </DotText>
        )
      },
      exportValue({ value }) {
        return value ? "Enabled" : "Disabled"
      },
    },
    CreatedAt: {
      id: "CreatedAt",
      label: "Created",
      defaultHidden: true,
      renderCell({ row }) {
        if (!row.original.CreatedAt) return null
        return formatDateString(String(row.original.CreatedAt))
      },
      exportValue({ row }) {
        if (!row.original.CreatedAt) return null
        return formatDateString(String(row.original.CreatedAt))
      },
    },
    UpdatedAt: {
      id: "UpdatedAt",
      label: "Updated",
      defaultHidden: true,
      renderCell({ row }) {
        if (!row.original.UpdatedAt) return null
        return formatDateString(String(row.original.UpdatedAt))
      },
      exportValue({ row }) {
        if (!row.original.UpdatedAt) return null
        return formatDateString(String(row.original.UpdatedAt))
      },
    },
  },
})

export type BasemapTableProps = Omit<
  TableProps<BasemapFragment>,
  "data" | "columns"
>

/**
 * Every runtime basemap, disabled ones included — withdrawing one is a
 * thing this tab is here to do, so it cannot hide them the way the
 * console's own read does.
 */
function BasemapTable(props: BasemapTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()
  const { data, previousData, loading, error, refetch } = useBasemapQuery({
    notifyOnNetworkStatusChange: true,
  })

  const displayData = data ?? previousData
  const basemaps = displayData?.getBasemaps ?? []

  // The rename dialog lives up here because the row toolbar that opens it
  // only exists while its row is hovered — a dialog mounted inside it would
  // unmount the moment the pointer leaves for the dialog.
  const [renameTarget, setRenameTarget] = useState<BasemapFragment | null>(null)

  return (
    <>
      <Table
        className={cn("basemap-table", className)}
        tableId="basemap-table"
        data={basemaps}
        columns={columns}
        initialColumnSizing={COLUMN_SIZING}
        enableRowSelection
        getHighlightedRowId={(row) => {
          return opener.getIsActive("basemap", String(row.original.FeatureCode))
        }}
        {...tableProps}
      >
        <Table.Toolbar>
          <div className="flex-1" />
          <Table.Search column="Name" placeholder="Search basemaps..." />
          <Separator orientation="vertical" />
          <Table.ToolbarMenu onRefresh={refetch} exportFileName="basemaps" />
        </Table.Toolbar>

        <Table.FilterList />

        <Table.RowToolbar<BasemapFragment> column="Name">
          {(basemap) => (
            <BasemapActionMenu
              basemap={basemap}
              onRename={() => setRenameTarget(basemap)}
            />
          )}
        </Table.RowToolbar>

        <Table.Content
          countLabel="basemaps"
          isLoading={loading}
          error={error}
        />

        <Table.SelectionToolbar>
          {(basemaps) => (
            <Table.ExportSelectionButton
              fileName="basemaps"
              selectionCount={basemaps.length}
            />
          )}
        </Table.SelectionToolbar>
      </Table>

      {renameTarget ? (
        <FeatureRenameDialog
          featureCode={renameTarget.FeatureCode}
          name={renameTarget.Name}
          open
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null)
          }}
        />
      ) : null}
    </>
  )
}

export default BasemapTable
